import clsx from 'clsx'
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'

export const inputClass =
  'w-full rounded-md border border-border bg-elevated px-md py-smd text-body text-text outline-none transition-colors focus:border-primary disabled:opacity-60'

export function Label({ children, className, hint }: { children: ReactNode; className?: string; hint?: ReactNode }) {
  return (
    <span className={clsx('mb-xs flex items-center justify-between text-body-sm font-medium text-text-secondary', className)}>
      {children}
      {hint ? <span className="text-caption text-text-muted">{hint}</span> : null}
    </span>
  )
}

export function Input({ label, hint, className, suffix, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode; hint?: ReactNode; suffix?: ReactNode }) {
  return (
    <label className={clsx('block', className)}>
      {label ? <Label hint={hint}>{label}</Label> : null}
      <span className="relative block">
        <input className={clsx(inputClass, suffix && 'pr-12')} {...rest} />
        {suffix ? <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-body-sm text-text-muted">{suffix}</span> : null}
      </span>
    </label>
  )
}

export function NumberInput({ label, hint, className, suffix, value, onValueChange, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  label?: ReactNode
  hint?: ReactNode
  suffix?: ReactNode
  value: string | number
  onValueChange: (v: string) => void
}) {
  return (
    <Input
      type="number"
      inputMode="decimal"
      step="any"
      label={label}
      hint={hint}
      className={className}
      suffix={suffix}
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      onFocus={(e) => e.target.select()}
      {...rest}
    />
  )
}

export function Textarea({ label, className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: ReactNode }) {
  return (
    <label className={clsx('block', className)}>
      {label ? <Label>{label}</Label> : null}
      <textarea className={clsx(inputClass, 'min-h-[88px] resize-y')} {...rest} />
    </label>
  )
}

export function Select({ label, className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { label?: ReactNode }) {
  return (
    <label className={clsx('block', className)}>
      {label ? <Label>{label}</Label> : null}
      <select className={clsx(inputClass, 'appearance-none bg-no-repeat pr-10')} style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='m6 9 6 6 6-6'/></svg>\")", backgroundPosition: 'right 12px center' }} {...rest}>
        {children}
      </select>
    </label>
  )
}

export function Toggle({ checked, onChange, label, description }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; description?: ReactNode }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} className="flex w-full items-center justify-between gap-md py-sm text-left">
      <span>
        <span className="block text-body text-text">{label}</span>
        {description ? <span className="block text-caption text-text-muted">{description}</span> : null}
      </span>
      <span className={clsx('relative h-7 w-12 shrink-0 rounded-full transition-colors', checked ? 'bg-primary' : 'bg-border')}>
        <span className={clsx('absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-transform', checked ? 'translate-x-6' : 'translate-x-1')} />
      </span>
    </button>
  )
}

/** Segmented control (the app's ViewTabBar / RNPickerSelect rows). */
export function Segmented<T extends string>({ value, options, onChange, className, size = 'md' }: {
  value: T
  options: { value: T; label: ReactNode }[]
  onChange: (v: T) => void
  className?: string
  size?: 'sm' | 'md'
}) {
  return (
    <div className={clsx('inline-flex max-w-full overflow-x-auto rounded-md bg-surface-inner p-xs hide-scrollbar', className)} role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          aria-selected={o.value === value}
          onClick={() => onChange(o.value)}
          className={clsx(
            'whitespace-nowrap rounded-sm font-medium transition-colors',
            size === 'sm' ? 'px-smd py-xs text-caption' : 'px-md py-sm text-body-sm',
            o.value === value ? 'bg-surface text-text shadow-sm' : 'text-text-secondary hover:text-text',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}
